"use client";
import React, { useEffect, useRef, useState } from "react";
import {
  TextField,
  InputAdornment,
  Paper,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Skeleton,
  Box,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useRouter } from "next/navigation"; // if you're on the Pages Router, change to next/router

export default function SearchBox({
  defaultValue = "",
  placeholder = "Search bands, venues, towns…",
  fullWidth = true,
}) {
  const router = useRouter();
  const [q, setQ] = useState(defaultValue);
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState({ bands: [], venues: [], events: [] });
  const [loading, setLoading] = useState(false);

  const abortRef = useRef(null);
  const boxRef = useRef(null);
  const timerRef = useRef(null);
  const cacheRef = useRef(new Map());

  // click-outside to close suggestions
  useEffect(() => {
    function onDocClick(e) {
      if (!boxRef.current?.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // debounced autocomplete
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    const val = q?.trim() || "";
    if (val.length < 2) {
      setSuggestions({ bands: [], venues: [], events: [] });
      setOpen(false);
      return;
    }

    timerRef.current = setTimeout(async () => {
      // cancel previous
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;

      const key = val.toLowerCase();
      const cached = cacheRef.current.get(key);
      if (cached) {
        setSuggestions(cached);
        setOpen(true);
        return;
      }

      try {
        setLoading(true);
        setOpen(true); // show skeleton immediately
        const r = await fetch(`/api/autocomplete?q=${encodeURIComponent(val)}`, {
          signal: ac.signal,
          cache: "no-store",
        });
        if (!r.ok) return;

        const data = await r.json();
        const next = {
          bands: data.bands || [],
          venues: data.venues || [],
          events: data.events || [],
        };

        cacheRef.current.set(key, next);
        setSuggestions(next);
      } catch {
        // ignore aborts
      } finally {
        setLoading(false);
      }
    }, 200);

    return () => clearTimeout(timerRef.current);
  }, [q]);

  function go() {
    const qq = q.trim();
    if (!qq) return;
    router.push(`/search?q=${encodeURIComponent(qq)}`);
    setOpen(false);
  }

  const hasAny =
    suggestions.bands.length || suggestions.venues.length || suggestions.events.length;

  return (
    <div
      ref={boxRef}
      style={{ position: "relative", width: fullWidth ? "100%" : 400, maxWidth: "100%" }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          go();
        }}
      >
        <TextField
          fullWidth={fullWidth}
          placeholder={placeholder}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => (hasAny || loading) && setOpen(true)} // include events & loading
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {loading ? <CircularProgress size={18} /> : null}
              </InputAdornment>
            ),
          }}
        />
      </form>

      {open && (hasAny || loading) ? (
        <Paper
          elevation={3}
          style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 10 }}
        >
          {loading && (
            <Box sx={{ p: 1.5 }}>
              <Skeleton height={22} />
              <Skeleton height={22} width="70%" />
            </Box>
          )}

          {suggestions.events.length > 0 && (
            <List
              dense
              subheader={<div style={{ padding: "6px 16px", fontWeight: 700 }}>Events</div>}
            >
              {suggestions.events.map((e, i) => (
                <ListItem
                  button
                  key={`e-${i}`}
                  onMouseDown={(ev) => ev.preventDefault()}
                  onClick={() => {
                    setOpen(false);
                    if (e.id) router.push(`/events/${e.id}`);
                    else {
                      setQ(e.title);
                      router.push(`/search?q=${encodeURIComponent(e.title)}`);
                    }
                  }}
                >
                  <ListItemText
                    primary={e.title}
                    secondary={[e.venue_name, [e.city, e.state].filter(Boolean).join(", ")]
                      .filter(Boolean)
                      .join(" — ")}
                  />
                </ListItem>
              ))}
            </List>
          )}

          {suggestions.bands.length > 0 && (
            <List
              dense
              subheader={<div style={{ padding: "6px 16px", fontWeight: 700 }}>Bands</div>}
            >
              {suggestions.bands.map((b, i) => (
                <ListItem
                  button
                  key={`b-${i}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setQ(b.name);
                    setOpen(false);
                    go();
                  }}
                >
                  <ListItemText primary={b.name} />
                </ListItem>
              ))}
            </List>
          )}

          {suggestions.venues.length > 0 && (
            <List
              dense
              subheader={<div style={{ padding: "6px 16px", fontWeight: 700 }}>Venues</div>}
            >
              {suggestions.venues.map((v, i) => (
                <ListItem
                  button
                  key={`v-${i}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setOpen(false);
                    if (v.internallink) {
                      // Navigate directly to venue page
                      const cleanLink = String(v.internallink).replace(/^\/|\/$/g, '');
                      router.push(`/venues/${cleanLink}`);
                    } else {
                      // Fallback to search
                      setQ(v.name + (v.city ? ` ${v.city}` : ""));
                      go();
                    }
                  }}
                >
                  <ListItemText primary={v.name} secondary={v.city} />
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      ) : null}
    </div>
  );
}
